/**
 * NBCOlympicsBadge — clickable NBC Tokyo 2020 vendor badge that
 * opens a full-screen modal of the official recommendation letter.
 *
 * Matches the visual design used on https://nojoenterprise.com/ —
 * a small Tokyo 2020 logo tile next to two lines of text, a purple
 * "Click to view the official letter" speech bubble that appears on
 * hover (pointing down at the badge), and a full-viewport purple
 * backdrop modal containing the letter image sized at 85vh.
 *
 * Used on:
 *   - HomePage hero
 *   - TestimonialsPage hero
 * Both pages render the identical experience by importing this
 * single component — no inline copies.
 *
 * Props:
 *   compact?: boolean
 *     When true (default), renders the side-by-side badge used in
 *     the testimonials and homepage heroes. No layout difference at
 *     present; reserved for future variants without API churn.
 */
import { useEffect, useState } from 'react';
import { useLanguage } from '@/lib/i18n';
import { X } from 'lucide-react';

// Letter image — hosted on Nojo's CDN. Both JTR and Nojo reference
// the same canonical file so a swap on Nojo's side propagates here
// automatically.
const NBC_LETTER_IMAGE =
  'https://nojoenterprise.com/badges/nbc/nbc-tokyo-2020.png';
// Small logo tile shown in the badge itself (square Tokyo 2020
// medallion). Same Nojo asset.
const NBC_LOGO_IMAGE =
  'https://nojoenterprise.com/badges/nbc/nbc-letter.png';

export default function NBCOlympicsBadge() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [modalOpen, setModalOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Body-scroll lock + Escape-to-close while the modal is open. The
  // letter image is height-constrained, so the body scroll lock is
  // mostly defensive — prevents background scroll on touch devices.
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen]);

  const tooltipLabel = ja
    ? '公式レターを表示'
    : 'Click to view the official letter';
  const labelTop = ja ? 'NBC オリンピックスベンダー' : 'NBC Olympics Vendor';
  const labelBottom = ja
    ? '東京2020で200枚超のETCカードを提供'
    : 'Provided 200+ ETC cards for the Tokyo 2020 Games';

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="relative inline-flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity group"
        data-testid="nbc-badge"
        aria-label={ja ? 'NBC オリンピックスからの推薦状を表示' : 'Open NBC Olympics recommendation letter'}
      >
        {/* Logo tile — square white card holding the Tokyo 2020 medallion. */}
        <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden group-hover:shadow-md transition-shadow flex-shrink-0">
          <img
            src={NBC_LOGO_IMAGE}
            alt="NBC Tokyo 2020"
            className="w-11 h-11 object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
        {/* Two-line label — left-aligned to mirror Nojo's hero badge. */}
        <div className="leading-tight text-left">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 group-hover:text-[#9200C7] transition-colors">
            {labelTop}
          </p>
          <p className="text-sm font-bold text-[#1F2540]">
            {labelBottom}
          </p>
        </div>

        {/* Purple speech-bubble tooltip — appears BELOW the badge on
            hover, with an upward-pointing arrow at the top of the
            bubble. On mobile the bubble auto-wraps inside a max
            width so it never overflows the viewport; on tablet+
            (`sm:`) it returns to a single nowrap line as designed. */}
        {hovered && (
          <div
            className="absolute top-full left-0 mt-3 z-[9998] pointer-events-none"
            data-testid="nbc-tooltip"
          >
            <div className="relative inline-block">
              {/* Upward-pointing arrow above the bubble */}
              <div
                className="absolute -top-1.5 left-7 w-3 h-3 bg-[#9200C7] rotate-45 shadow-lg"
                aria-hidden="true"
              />
              <div className="relative bg-[#9200C7] text-white text-xs sm:text-sm font-bold px-4 py-2.5 rounded-2xl shadow-xl max-w-[240px] sm:max-w-none sm:whitespace-nowrap leading-snug">
                {tooltipLabel}
              </div>
            </div>
          </div>
        )}
      </button>

      {/* Full-viewport modal. z-[10000] sits above the sticky site
          header (z-[200]) AND the chat / email / status widgets
          (z-[9999]). Letter is height-capped at 85vh so the whole
          page fits without internal scrolling. Click backdrop or X
          (or press Escape) to dismiss. */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#9200C7]/90 backdrop-blur-sm p-4 sm:p-6"
          data-testid="nbc-modal"
          role="dialog"
          aria-modal="true"
          aria-label={ja ? 'NBC オリンピックス推薦状' : 'NBC Olympics recommendation letter'}
          onClick={() => setModalOpen(false)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setModalOpen(false); }}
            className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[10001] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white text-gray-800 shadow-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
            aria-label={ja ? '閉じる' : 'Close'}
            data-testid="close-nbc-modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2.4} />
          </button>
          <img
            src={NBC_LETTER_IMAGE}
            alt={ja ? 'NBC オリンピックスからの推薦状（東京2020）' : 'NBC Olympics recommendation letter (Tokyo 2020)'}
            className="block max-h-[85vh] w-auto h-auto max-w-full rounded-lg shadow-2xl bg-white"
            data-testid="nbc-letter-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
